import { I18n } from '@calendly/i18n'
import { colorTextDisabled } from '@calendly/ui/theme'
import { groupBy, isEmpty, camelCase, isString } from 'lodash-es'
import moment from 'moment-timezone'

import { convertHostIdsToMeetingRoles } from 'components/meetings'
import {
  ANALYTICS_MAP,
  allValue,
  allowedScopeTypes,
  nonHostValue,
  Periods,
  SchedulingMethods,
  Statuses
} from 'constants/meetings'
import { getVideoLocationKinds, getVideoLocationTypes } from 'features/meeting_intelligence'
import { camelize } from 'services/string_utils'
import { mapScopeTypeToBackendValue } from 'shared/api/meetings/utils'

import { normalizeEventGuestEmails } from './normalize_event_guest_emails'

// Re-export from canonical source (used by features/meetings/index.js)
export {
  isPendingEvent,
  transformLocationKind,
} from 'components/meetings'

export { mapScopeTypeToBackendValue }

export const buildSearchFilters = (scopeType, scopeId, filterParams) => {
  if (!filterParams) return null

  const validFilterProperties = new Set([
    'period',
    'past',
    'startDate',
    'endDate',
    'inviteeEmails',
    'eventTypeIds',
    'packageUuids',
    'schedulingMethods',
    'statusIds',
    'uuid',
    'userIds',
    'teamIds',
    'utmCampaign',
    'utmContent',
    'utmMedium',
    'utmSource',
    'utmTerm',
    'scopeType',
    'scopeId',
    'searchKeyword',
    'meetingIntelligenceStatus'
  ])

  const camelizedParams = camelize({
    ...filterParams,
    scopeType,
    scopeId,
  })

  const filters = {}
  const roles = convertHostIdsToMeetingRoles(filterParams.host_ids, scopeType, scopeId)
  if (roles) {
    filters.role = roles
  }

  if (scopeType === 'users' && !scopeId) {
    filters.ownerType = 'USER'
  } else if (scopeType === 'teams' && !scopeId) {
    filters.ownerType = 'TEAM'
  }

  if (filterParams.start_date && filterParams.end_date) {
    filters.startDate = filterParams.start_date
    filters.endDate = filterParams.end_date
  }

  for (const [key, value] of Object.entries(camelizedParams)) {
    if (validFilterProperties.has(key) && value != null && (!Array.isArray(value) || value.length > 0)) {
      filters[key] = value
    }
  }

  if (filterParams.search_keyword) {
    filters.searchKeyword = filterParams.search_keyword
  }

  if (filters?.scopeType) {
    filters.scopeType = mapScopeTypeToBackendValue(filters.scopeType, filters?.scopeId)

    if (!allowedScopeTypes.includes(filters.scopeType)) {
      filters.scopeType = ''
    }

    if (filters.scopeType === 'my_calendly') {
      filters.scopeId = ''
    }
  }

  if (!(scopeType === 'user' && scopeId === 'me')) {
    filters.schedulingMethods = [SchedulingMethods.CALENDLY]
  }

  return filters
}

export const isSupportedLocation = (event, hasMsTeamsFlag = false) => {
  const eligibleLocationKinds = getVideoLocationKinds(hasMsTeamsFlag)
  const eligibleLocationTypes = getVideoLocationTypes(hasMsTeamsFlag)
  return eligibleLocationKinds.includes(event?.locationKind)
    || eligibleLocationTypes.includes(event?.locationType)
    || eligibleLocationKinds.includes(event?.location_kind)
    || eligibleLocationTypes.includes(event?.location_type)
}

/** Solo vs group for list UI: trust API event_type.kind when group (group ET may have only one booking). */
const listKindFromEventTypeAndInvitees = (camelEventType, inviteeCount) => {
  const raw = camelEventType?.kind
  if (typeof raw === 'string' && raw.toLowerCase() === 'group') return 'group'
  return inviteeCount > 1 ? 'group' : 'solo'
}

/** Google Calendar room/equipment resources are synced as INVITEE rows with @resource.calendar.google.com emails. */
const isGoogleCalendarResourceEmail = (email) => (
  isString(email) && email.endsWith('@resource.calendar.google.com')
)

const isGoogleCalendarResourceInvitee = (attendee) => (
  attendee?.kind === 'INVITEE' && isGoogleCalendarResourceEmail(attendee.email)
)

export const mapNodeToMeeting = (node, publisherTZ, hasMsTeamsFlag = false, currentTime = moment()) => {
  let attendees = (node.attendees || []).map((attendee) => ({ ...attendee, policy: { noShow: false, update: false } }))
  // Supergraph (and some detail payloads) expose a singular `invitee` instead of `attendees`.
  if (attendees.length === 0 && node.invitee && typeof node.invitee === 'object') {
    const camelInvitee = camelize(node.invitee)
    attendees = [{
      ...camelInvitee,
      kind: camelInvitee.kind || 'INVITEE',
      policy: { noShow: false, update: false },
    }]
  }
  attendees = attendees.filter((attendee) => !isGoogleCalendarResourceInvitee(attendee))

  const groupedAttendees = groupBy(attendees, 'kind')
  const allInvitees = groupedAttendees.INVITEE || []
  const invitees = allInvitees.filter((inv) => !inv.cancelled)
  const hosts = groupedAttendees.HOST || []
  const cohosts = groupedAttendees.COHOST || []
  const externalAttendees = groupedAttendees.EXTERNAL || []
  const eventTypeBase = camelize(node.event_type || {})

  const serverPolicy = camelize(node.policy || {})
  const policy = {
    update: false,
    ...serverPolicy,
    addAiNotetaker: isSupportedLocation(node, hasMsTeamsFlag) && !node.cancelled,
  }

  // Filled / capacity from cmd-api and supergraph:
  // - `??` is not enough: `filled_count: 0` is a defined value, so it blocks `invitees_count` (which can be 2).
  //   When the API sends that mismatch, trust `invitees_count` (matches `INVITEE` count on the node).
  // - If `filled_count` is truly absent, still fall back to `invitees_count`, then to attendee length.
  const rawFilled = node.filled_count ?? node.filledCount
  const rawInviteesCount = node.invitees_count ?? node.inviteesCount
  const isZeroFilledButNonZeroInviteeCount = (rawFilled === 0 || rawFilled === '0')
    && rawInviteesCount != null
    && rawInviteesCount !== ''
    && Number(rawInviteesCount) > 0
  let filledFromNode
  if (isZeroFilledButNonZeroInviteeCount) {
    filledFromNode = rawInviteesCount
  } else if (rawFilled != null && rawFilled !== '') {
    filledFromNode = rawFilled
  } else {
    filledFromNode = rawInviteesCount
  }
  const filledCountFromApi = filledFromNode != null && filledFromNode !== ''
  const filledCount = filledCountFromApi
    ? filledFromNode
    : invitees.length

  // `event_type.invitees_limit` can be 0 while the meeting has a real cap on `invitees_limit` (supergraph/cmd).
  const nRootLimit = node.invitees_limit ?? node.inviteesLimit
  const nEtLimit = eventTypeBase.inviteesLimit
  const numberOrNull = (v) => {
    if (v == null || v === '') return null
    const n = Number(v)
    return Number.isNaN(n) ? null : n
  }
  const rootLimitN = numberOrNull(nRootLimit)
  const etLimitN = numberOrNull(nEtLimit)
  const resolvedInviteesLimit = (() => {
    if (rootLimitN != null && rootLimitN > 0) return rootLimitN
    if (etLimitN != null && etLimitN > 0) return etLimitN
    if (rootLimitN != null) return rootLimitN
    if (etLimitN != null) return etLimitN
    return rootLimitN ?? etLimitN
  })()

  const updated = {
    ...camelize(node),
    startTime: moment(node.start_time || node.startTime).tz(publisherTZ).format(),
    endTime: moment(node.end_time || node.endTime).tz(publisherTZ).format(),
    source: node.kind,
    eventType: {
      ...eventTypeBase,
      // Only treat as external (generic color) when kind is explicitly non-Calendly.
      // Missing `kind` (e.g. some detail queries) must not trigger this — `undefined !== 'CALENDLY'` is true.
      ...(node.kind != null && node.kind !== 'CALENDLY' ? { color: colorTextDisabled } : {}),
      kind: listKindFromEventTypeAndInvitees(eventTypeBase, allInvitees.length),
    },
    policy,
    invitee: invitees[0] || {},
    filledCount,
    // When the API omits filled_count, we derive from invitees; thin payloads can yield 0 incorrectly.
    // defensiveMergeMeeting uses this to avoid clobbering a known-good REST/cache count with that 0.
    ...(filledCountFromApi ? {} : { filledCountDerivedFromInvitees: true }),
    eventMemberships: hosts
      .concat(cohosts)
      .concat(externalAttendees)
      .filter(Boolean),
    invitees,
    attendees,
    host: hosts[0],
    past: moment(node.end_time || node.endTime).isBefore(currentTime),
    ...(resolvedInviteesLimit == null ? {} : { inviteesLimit: resolvedInviteesLimit }),
  }
  return {
    ...updated,
    eventGuestEmails: normalizeEventGuestEmails(updated),
  }
}

export const mapChangedFiltersForAnalytics = (changedFilters) => {
  if (isEmpty(changedFilters)) return []
  if (!ANALYTICS_MAP) return []

  return changedFilters
    .map(camelCase)
    .filter((filter) => filter in ANALYTICS_MAP)
    .map((filter) => ANALYTICS_MAP[filter])
}

const UTM_PARAMS = ['utm_campaign', 'utm_content', 'utm_medium', 'utm_source', 'utm_term']

export const isFilterSet = (filterParams) => {
  if (filterParams.period !== Periods.UPCOMING) return true

  // Check if status is different from default (just 'active')
  const isDefaultStatus = filterParams.status_ids?.length === 1 && filterParams.status_ids[0] === Statuses.ACTIVE
  if (!isDefaultStatus) return true

  if (filterParams.event_type_ids?.length > 0) return true
  if (filterParams.package_uuids?.length > 0) return true
  if (filterParams.host_ids?.includes(allValue) || filterParams.host_ids?.includes(nonHostValue)) return true
  if (filterParams.invitee_emails?.length > 0) return true
  if (filterParams.team_ids?.length > 0) return true
  if (filterParams.user_ids?.length > 0) return true

  // Check UTM/Tracking ID parameters
  if (UTM_PARAMS.some((param) => filterParams[param]?.length > 0)) return true

  if (filterParams.search_keyword) return true
  if (filterParams.meeting_intelligence_status === 'COMPLETED') return true
  return false
}

export const EXTERNAL_SOURCES = {
  CALENDLY: 'CALENDLY',
  GOOGLE: 'GOOGLE',
  OUTLOOK: 'OUTLOOK'
}

/** True for connected-calendar sources (Google, Outlook); host is determined by email. */
export const isExternalCalendarSource = (source) => source === EXTERNAL_SOURCES.GOOGLE || source === EXTERNAL_SOURCES.OUTLOOK

/** Matches persistent filter UI: single `calendly` selection (internal / scheduling only). */
export const isCalendlyOnlySchedulingMethods = (schedulingMethods) => (
  Array.isArray(schedulingMethods)
  && schedulingMethods.length === 1
  && schedulingMethods.includes(SchedulingMethods.CALENDLY)
)

/** Connected-calendar only (Google and/or Outlook, no Calendly). */
export const isExternalOnlySchedulingMethods = (schedulingMethods) => {
  if (!Array.isArray(schedulingMethods) || schedulingMethods.length === 0) return false
  if (schedulingMethods.includes(SchedulingMethods.ALL)) return false
  const hasCalendly = schedulingMethods.includes(SchedulingMethods.CALENDLY)
  const hasExternal = schedulingMethods.includes(SchedulingMethods.GOOGLE)
    || schedulingMethods.includes(SchedulingMethods.OUTLOOK)
  return hasExternal && !hasCalendly
}

const meetingSourceKind = (event) => event?.source ?? event?.kind

/** Drop stale cached rows that do not match the active scheduling-method scope. */
export const filterMeetingsDateGroupsBySchedulingMethods = (dateGroups, schedulingMethods) => {
  if (!Array.isArray(dateGroups)) return dateGroups

  const calendlyOnly = isCalendlyOnlySchedulingMethods(schedulingMethods)
  const externalOnly = isExternalOnlySchedulingMethods(schedulingMethods)
  if (!calendlyOnly && !externalOnly) return dateGroups

  return dateGroups
    .map((group) => ({
      ...group,
      events: (group.events || []).filter((event) => {
        const source = meetingSourceKind(event)
        if (calendlyOnly) return source === EXTERNAL_SOURCES.CALENDLY
        if (externalOnly) return isExternalCalendarSource(source)
        return true
      }),
    }))
    .filter((group) => group.events.length > 0)
}

/**
 * Display name for meeting card “hosted by” / host resolution: single membership, joined multi-host
 * (collective / round-robin), or top-level host when memberships are absent.
 */
export const getMeetingCardHostDisplayName = (event) => {
  if (!event) return null
  const { eventMemberships = [], host } = event
  const multiHost = Boolean(event.isCollective || event.isRoundRobin || event.isCollectiveRoundRobin)
  if (multiHost && eventMemberships.length > 0) {
    const parts = eventMemberships.map((m) => m.name || m.email).filter(Boolean)
    if (parts.length > 0) return parts.join(', ')
  }
  if (eventMemberships.length > 0) {
    const m = eventMemberships[0]
    return m.name || m.email || null
  }
  return host?.name || host?.email || null
}

/**
 * Invitee / group attendee line for Calendly-sourced meetings (host or delegated teammate view).
 */
const buildCalendlyInviteeOrGroupLine = ({
  source,
  invitees,
  isGroup,
  filledCount,
}) => {
  const isExternalMeeting = isExternalCalendarSource(source)
  const list = invitees ?? []

  if (isGroup) {
    const raw = list.length || (filledCount ?? 0)
    const count = Number(raw) || 0
    return I18n.t('publisher.meetings.link.group.unlimited', { count })
  }

  const firstInvitee = list[0]
  if (!firstInvitee) return null

  const inviteeName = isExternalMeeting || !firstInvitee.name
    ? firstInvitee.email
    : firstInvitee.name

  if (!inviteeName) return null

  return I18n.t('publisher.meetings.link.with', { name: inviteeName })
}

/**
 * Secondary line for meeting cards: optional “hosted by …”, then solo/group invitee copy.
 *
 * Pass `scopeType` / `scopeId` from `useScope` on meetings list cards. Callers that omit
 * both (e.g. recaps) keep legacy non-host copy: “hosted by …” only, without appending invitee/group text;
 * host callers without scope keep invitee/group line only (no “hosted by” prefix).
 *
 * @returns {string | null}
 */
export const getParticipantText = ({
  isHostMeeting,
  source,
  invitees,
  host,
  scopeType,
  scopeId,
  isGroup,
  filledCount,
}) => {
  const isExternalMeeting = isExternalCalendarSource(source)
  const hostDisplay = host?.name || host?.email

  if (!isHostMeeting) {
    if (isExternalMeeting) return null
    if (!hostDisplay) return null

    const isMyCalendly = scopeType === 'user' && scopeId === 'me'
    const isTeammateUserTab = scopeType === 'user' && scopeId != null && scopeId !== 'me'

    const hostedPrefix = I18n.t('publisher.meetings.hosted_by', { name: hostDisplay })

    if (isTeammateUserTab) {
      return buildCalendlyInviteeOrGroupLine({
        source,
        invitees,
        isGroup,
        filledCount,
      })
    }

    if (isMyCalendly) {
      if (isGroup) {
        const rest = buildCalendlyInviteeOrGroupLine({
          source,
          invitees,
          isGroup,
          filledCount,
        })
        return `${hostedPrefix} ${rest}`
      }
      return hostedPrefix
    }

    // Meetings list always passes scope from useScope. Omitting both (e.g. recaps MeetingDetails)
    // matches pre–meetings-list refactor: non-host internal meetings show hosted-by only.
    if (scopeType == null && scopeId == null) {
      return hostedPrefix
    }

    const rest = buildCalendlyInviteeOrGroupLine({
      source,
      invitees,
      isGroup,
      filledCount,
    })
    return rest ? `${hostedPrefix} ${rest}` : hostedPrefix
  }

  const rest = buildCalendlyInviteeOrGroupLine({
    source,
    invitees,
    isGroup,
    filledCount,
  })

  // Host on My Calendly: omit “hosted by” (it’s obviously yours). Team / all-users / etc. still
  // show host so group cards read like non-host rows. Recaps omit scope → invitee line only.
  const isMyCalendly = scopeType === 'user' && scopeId === 'me'
  const hasExplicitScope = scopeType != null || scopeId != null
  if (
    !isExternalMeeting
    && hostDisplay
    && hasExplicitScope
    && !isMyCalendly
  ) {
    const hostedPrefix = I18n.t('publisher.meetings.hosted_by', { name: hostDisplay })
    return rest ? `${hostedPrefix} ${rest}` : hostedPrefix
  }

  return rest
}

export const getEventLocationIfURL = (location) => {
  if (!isString(location)) return null

  if (location.match(/^https?:\/\//)) {
    return location
  }

  return null
}
